import { Add, Check, Search } from "@mui/icons-material";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { Alert, AlertColor, Box, Button, Divider, IconButton, MenuItem, Select, SelectChangeEvent, Snackbar, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from 'react-router-dom';
import { Group } from "../../../../../model/GroupModel";
import { GetOneOrganization, GetOrganizationUserTrim, GetOrganizationUserTrimName } from "../../../../../services/OrganizationServices";
import { Organization, User } from "../../../../../model/OrganizationModel";
import SaveIcon from '@mui/icons-material/Save';
import UsersTable from "../UsersTable/UsersTable";
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { CreateGroup } from "../../../../../services/GroupsServices";
import { UserGroupsVO } from "../../../../../model/UserModel";
import { AddUserGroup } from "../../../../../services/UserServices";
import { useProfile } from "../../../../../context/ProfileProvider/useProfile";
import KeydriveSnackbar from "../../../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar";

export const GroupPanel = () => {
    const [groupName, setGroupName] = useState<string>();
    const [isGroupNameError, setIsGroupNameError] = useState<boolean>(false);
    const [organization, setOrganization] = useState<Organization | undefined>();
    const [messageSnack, setMessageSnack] = useState<string | undefined>(undefined);
    const [colorSnack, setColorSnack] = useState<AlertColor | undefined>();
    const [organizationUsers, setOrganizationUsers] = useState<User[]>([]);
    const [users, setUsers] = useState<string[]>([]);
    const [userSelected, setUserSelected] = useState<User | undefined>();
    const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
    const [search, setSearch] = useState<string>('')
    const skip = 0;
    const limit = 50;

    const { t } = useTranslation(); 
    const navigate = useNavigate();
    const { nameOrg } = useParams();
    const profile = useProfile();

    const handleSnackbarWithMessage = (message: string, color: AlertColor) => {
        setMessageSnack(message)
        setColorSnack(color)
    }

    const handleBackClick = () => {
        navigate(`/${nameOrg}/adminConsole/usersAndGroups`);
    };

    const handleChangeUserSelected = (event: SelectChangeEvent<string>) => {
        const selectedUser = organizationUsers.find(user => user.id === event.target.value);
        if (selectedUser) {
            setUserSelected(selectedUser);
        }
    };


    const addUserToGroup = (userGroup: UserGroupsVO) => {
        AddUserGroup(userGroup)
            .then(() => handleSnackbarWithMessage("Usuário adicionado com sucesso", "success"))
            .catch((err) => handleSnackbarWithMessage(err.response.data, 'error'))
    }

    const handleSaveGroup = () => {

        if (!groupName || groupName.length < 3) {
            setIsGroupNameError(true);
            return;
        }
        
        const group:Group= {
            name: groupName,
            organizationId: organization?.id,
            users: users,
            owner: profile.id
        } 

        CreateGroup(group)
        .then((data) => {
            group.users?.forEach((user)=> {
                const userGroup: UserGroupsVO = { 
                    groupId: data,
                    userId: user
                }
                addUserToGroup(userGroup);
            })
            handleSnackbarWithMessage(t("AdminConsole.GroupCreated"), 'success')

            setTimeout(() => {
                 navigate(`/${nameOrg}/adminConsole/usersAndGroups/${data}`);
            }, 3000)
        })
        .catch((err) => handleSnackbarWithMessage(err.response.data, 'error'));
    }

    const handleChangeGroupName = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value != undefined) {
            setGroupName(event.target.value);
        }
    }
    
    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value != undefined) {
            setSearch(event.target.value);
        }
    }

    const handleAddUser = () => {
        if (userSelected) {
            setUsers((prevValue) => [...prevValue, userSelected.id]);
            setSelectedUsers((prevValue) => [...prevValue, userSelected]); 
            setOrganizationUsers((prevValue) =>
                prevValue.filter(user => user.id !== userSelected.id)
            );
            setUserSelected(undefined); 
        }
    };

    const handleRemoveUser = (userId: string) => {
        setUsers((prevValue) =>
            prevValue.filter(user => user !== userId)
        );

        const removedUser = selectedUsers.find(user => user.id === userId);
        if (removedUser) {
            setOrganizationUsers((prevValue) => [...prevValue, removedUser]);
            setSelectedUsers((prevValue) =>
                prevValue.filter(user => user.id !== userId) 
            );
        }
    };

    useEffect(() => {
        if (nameOrg) {
            GetOneOrganization(nameOrg)
                .then((data) => {
                    if (data) {
                        setOrganization(data)
                    }
                }).catch((err) => handleSnackbarWithMessage(err.response.data, 'error'));


            if(search == ''){
                GetOrganizationUserTrim(nameOrg, skip, limit)
                .then((data) => {
                    setOrganizationUsers(data)
                }).catch((err) => handleSnackbarWithMessage(err.response.data, 'error'));
            } else {
                GetOrganizationUserTrimName(nameOrg, skip, limit, search)
                .then((data) => {
                    setOrganizationUsers(data)
                }).catch((err) => handleSnackbarWithMessage(err.response.data, 'error'));
            }
        }
    }, [nameOrg])

    const styles = {
        container: {
            background: '#F7F8FA',
            padding: '30px',
        },
        panel: {
            background: '#FFFFFF',
            border: '1px solid #0000001A',
            borderRadius: '4px',
            height: '100%',
            padding: '30px',
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
        },
        iconButton: {
            borderRadius: '4px',
            font: 'normal normal normal 14px / 21px Segoe UI',
            color: "#22638D",
            '&:hover': {
                backgroundColor: "#F4F7F9",
            },
        },
        headerText: {
            display: 'flex',
            alignItems: 'center',
            font: 'normal normal 600 20px/ 27px Segoe UI',
        },
        saveButton: {
            height: '36px',
            backgroundColor: '#22638D',
            textTransform: 'none',
            font: 'normal normal normal 14px/21px Segoe UI',
            whiteSpace: 'nowrap',
        },
        cancelButton: {
            height: '36px',
            textTransform: 'none',
            font: 'normal normal normal 14px/21px Segoe UI',
            whiteSpace: 'nowrap',
        },
        sectionTitle: {
            font: 'normal normal 600 16px/ 24px Segoe UI',
        },
        label: {
            font: 'normal normal normal 12px / 18px Segoe UI',

        },
        textField: {
            width: '100%',
            borderBottom: '1px solid black',

            '&.MuiTextField-root': {
                border: 'none',
            },
        },
        description: {
            font: 'normal normal normal 14px / 21px Segoe UI',
            opacity: 0.7,
        },
        switch: {
            '& .MuiSwitch-switchBase': {
                color: '#969696',
                '&.Mui-checked': {
                    color: '#22638D',
                },
            },
            '& .MuiSwitch-track': {
                backgroundColor: '#BDBDBD',
            },
        },
        twoFaText: {
            font: 'normal normal normal 14px / 21px Segoe UI',
            opacity: 0.5,
        },
    };

    return (
        <>
            <Box sx={styles.container}>
                <Box sx={styles.panel}>
                    <Box>
                        <IconButton sx={styles.iconButton} onClick={handleBackClick}>
                            <ArrowBackIosIcon sx={{ height: '12px' }} />
                            <Typography>{t("AdminConsole.Back")}</Typography>
                        </IconButton>
                    </Box>

                    <Box display={'flex'} justifyContent={'space-between'}>
                        <Typography sx={styles.headerText}>
                            <GroupAddIcon sx={{ width: '24px', height: '24px', marginRight: '12px', color: '#22638D' }} />
                            {t("AdminConsole.NewGroup")}
                        </Typography>

                        <Button variant="contained" startIcon={<Check />} onClick={handleSaveGroup} sx={styles.saveButton}>
                            {t("AdminConsole.Save")}
                        </Button>
                    </Box>

                    <Divider />

                    <Typography sx={styles.sectionTitle}>{t("AdminConsole.GroupsInfo")}</Typography>
                    <Box flex={1}>
                        <Typography sx={styles.label}>{t("AdminConsole.GroupName")}</Typography>
                        <TextField
                            error={isGroupNameError ? true : false}
                            value={groupName}
                            onChange={handleChangeGroupName}
                            sx={styles.textField}
                            required
                            variant="standard"
                            placeholder={t("AdminConsole.TypeGroupName")}
                            helperText={isGroupNameError ? t("AdminConsole.InvalidUsername") : undefined}
                        />
                    </Box>

                    <Divider sx={{marginTop: '14px'}} />

                    <Typography sx={styles.sectionTitle}>{t("AdminConsole.Participants")}</Typography>
                    <Box sx={{
                        marginTop: '20px',
                        height: '36px',
                        display: 'flex',
                        gap: '20px',
                        padding: '0px',
                        marginBottom: '20px',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <TextField
                            fullWidth
                            id="filled-basic"
                            variant="filled"
                            placeholder={t('AdminConsole.WhatAreYouLookingFor')}
                            onChange={handleSearch}
                            InputProps={{
                                disableUnderline: true,
                                startAdornment: (
                                    <Box sx={{ display: 'flex', alignItems: 'center', paddingRight: '10px', opacity: '0.5' }}>
                                        <Search />
                                    </Box>
                                ),
                                sx: {
                                    height: '36px',
                                    padding: '0 10px',
                                    '& .MuiFilledInput-input': {
                                        height: '36px',
                                        padding: '0 10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                },
                            }}
                            sx={{
                                flex: 1,
                                marginTop: '14px',
                                '& .MuiInputBase-root': {
                                    height: '36px',
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '4px',
                                },
                                '& input::placeholder': {
                                    fontSize: '14px',
                                    color: '#969696',
                                },
                            }}
                        />
                        <Box sx={{flex: 1, width: '100%'}}>
                            <Typography sx={styles.label}>{t("AdminConsole.User")}</Typography>
                            <Select
                                sx={{ width: '100%' }}
                                variant="standard"
                                value={userSelected ? userSelected.id : ""}
                                onChange={handleChangeUserSelected}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return (
                                            <Typography
                                                sx={{
                                                    font: 'normal normal normal 14px / 21px Segoe UI',
                                                    color: '#2D2D2D',
                                                    opacity: 0.2,
                                                    marginLeft: '5px'
                                                }}
                                            >
                                                {t("AdminConsole.Select")}
                                            </Typography>
                                        );
                                    }

                                    return (
                                        <>
                                            <Typography
                                                sx={{
                                                    font: 'normal normal normal 14px / 21px Segoe UI',
                                                    color: '#2D2D2D',
                                                    marginLeft: '5px',
                                                    display: 'flex'
                                                }}
                                            >
                                                {userSelected?.name}
                                                <Typography component = "span" sx = {{ opacity: 0.5, marginLeft: '4px' }}>
                                                    ({userSelected?.login})
                                                </Typography>
                                            </Typography>
                                        </>
                                    );
                                }}
                            >
                                {organizationUsers.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.name}
                                        <Typography component="span" sx={{ opacity: 0.5, marginLeft: '4px' }}>
                                            ({user.login})
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                        <Button variant="contained" startIcon={<Check />} onClick={handleAddUser} sx={styles.saveButton}>
                            {t("AdminConsole.AddUser")}
                        </Button>
                    </Box>

                    <Box sx={{ borderRadius: '8px', border: '1px solid #EFEFEF', minHeight: '300px'}}>
                        {
                            users.length === 0 ? (
                                <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                    <Box sx={{ background: "#E8EFF3", borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: "center" }}>
                                        <PersonOffIcon sx={{ color: '#22638D' }} />
                                    </Box>
                                    <Typography sx={{ font: 'normal normal 600 14px/ 21px Segoe UI' }}>
                                        {t("AdminConsole.NoUsers")}
                                    </Typography>
                                    <Typography sx={{ font: "normal normal normal 14px/21px Segoe UI" }}>
                                        {t("AdminConsole.AddUsersToGroup")}
                                    </Typography>
                                </Box>
                            ) :
                                <UsersTable nameOrg={nameOrg!} search={search} isGroupPage={true} usersIdList={users} removeFromGroup={handleRemoveUser}/>
                        }
                    </Box>
                </Box>

                <KeydriveSnackbar 
                    message={messageSnack}
                    open={messageSnack != undefined}
                    variant={colorSnack}
                    onClose={() => setMessageSnack(undefined)}
                />
            </Box>
        </>
    )
}